import React, { useContext, useEffect, useState } from 'react'
import { Card, Dimmer, Image, Loader, Icon } from 'semantic-ui-react'
import { Service } from '../../types/Service';
import UserContext from '../../../contexts/UserContext';
import { Document } from '../../types/Document';
import sendToast from '../../../utils/Toast';
import { getAssetById } from '../../services/Asset';
import { resolveServiceDocuments } from '../../services/Documents';
import { getAttachedFileLink } from '../../utils/document';

interface ServiceCardProps {
    service: Service;
    serviceImage: string;
    asset?: string;
    source?: string;
    /** Compact mode: service name, short description, and right-side image only. No asset, docs, or price. */
    compact?: boolean;
    /** Whether the facility has payments enabled. Payment-required visual treatment lands with the order-wizard payment gating work. */
    facilityPaymentsEnabled?: boolean;
}

const assetDetailsCache = new Map<string, { assetName: string; assetUrl: string }>();
const assetDetailsInFlight = new Map<string, Promise<{ assetName: string; assetUrl: string }>>();

const ServiceCard: React.FC<ServiceCardProps> = ({ service, serviceImage, asset = '', source, compact = false, facilityPaymentsEnabled = false }) => {
    const [serviceDocs, setServiceDocs] = useState<Document[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [assetName, setAssetName] = useState<string>(asset);
    const [assetUrl, setAssetUrl] = useState<string>('');

    const {
        userObject: {
            registrants: { list: listOfResidents, activeRegistrantsIndexes },
        },
    } = useContext(UserContext);

    const activeResident = listOfResidents[activeRegistrantsIndexes[0]]; // get the first resident from the list of active residents because neighbors web only supports the resident who is logged in and we can't deselect or change it
    const activeFacilityId = activeResident?.Facility;

    useEffect(() => {
        const fetchServiceDocs = async () => {
            try {
                if (!activeFacilityId) {
                    setServiceDocs([]);
                    return;
                }
                setLoading(true);
                const docs = await resolveServiceDocuments(activeFacilityId, service);
                setServiceDocs(docs);
            } catch (error) {
                setServiceDocs([]);
                console.error('Error fetching service document:', error);
                sendToast('error', error instanceof Error ? error.message : 'An error occurred while fetching service document');
            } finally {
                setLoading(false);
            }
        }
        fetchServiceDocs();
    }, [service?._id, service.DocIds, activeFacilityId]);

    useEffect(() => {
        const fetchAssetDetails = async () => {
            try {
                if (!activeFacilityId) {
                    setAssetName('');
                    setAssetUrl('');
                    return;
                }
                if (asset) {
                    setAssetName(asset);
                    return;
                }
                if (!service || !service.Asset) {
                    setAssetName('');
                    setAssetUrl('');
                    return;
                }
                const cacheKey = `${activeFacilityId}:${service.Asset}`;
                const cachedAsset = assetDetailsCache.get(cacheKey);
                if (cachedAsset) {
                    setAssetName(cachedAsset.assetName);
                    setAssetUrl(cachedAsset.assetUrl);
                    return;
                }

                let request = assetDetailsInFlight.get(cacheKey);
                if (!request) {
                    request = (async () => {
                        const assetRes = await getAssetById({
                            Facility: activeFacilityId,
                            AssetId: service.Asset,
                        });
                        if (assetRes && assetRes.response) {
                            return {
                                assetName: assetRes.response.AssetName || '',
                                assetUrl: assetRes.response.URL || '',
                            };
                        }
                        return { assetName: '', assetUrl: '' };
                    })();
                    assetDetailsInFlight.set(cacheKey, request);
                }

                const resolvedAsset = await request;
                assetDetailsCache.set(cacheKey, resolvedAsset);
                setAssetName(resolvedAsset.assetName);
                setAssetUrl(resolvedAsset.assetUrl);
            } catch (error) {
                sendToast('error', error instanceof Error ? error.message : 'An error occurred while fetching asset details');
                setAssetName('');
                setAssetUrl('');
            } finally {
                if (activeFacilityId && service?.Asset) {
                    assetDetailsInFlight.delete(`${activeFacilityId}:${service.Asset}`);
                }
            }
        };

        fetchAssetDetails();
    }, [asset, compact, service, activeFacilityId]);

    const shortDesc = service.shortDescription || '';
    const name = service.name || '';
    const isSingleWord = !shortDesc.includes(' ');
    const isTooLong = shortDesc.length > 25;

    const displayDescription = isSingleWord && isTooLong
        ? `${shortDesc.slice(0, 15)}...`
        : shortDesc;

    const isShortDescriptionAndNameMaxed = shortDesc.length + name.length > 40;

    if (compact) {
        return (
            <Card
                className="cardContainer serviceCardCompact"
                style={{
                    marginTop: '6px',
                    border: '2px solid #A8DDFD',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    width: '100%',
                    minHeight: '160px',
                }}
            >
                <Card.Content style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '12px 16px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Card.Header style={{ padding: '0px', fontSize: '30px', fontWeight: 700 }}>
                            {service.name}
                        </Card.Header>
                        <Card.Description style={{ marginTop: '18px', fontSize: '20px' }}>
                            {displayDescription}
                        </Card.Description>
                        {assetName && (
                            <Card.Meta style={{ marginTop: '8px', fontSize: '14px', color: 'black' }}>
                                Asset:{' '}
                                {assetUrl ? (
                                    <a
                                        href={assetUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ color: '#0066cc', textDecoration: 'underline' }}
                                    >
                                        <span style={{ fontWeight: 'bold' }}>{assetName}</span>
                                    </a>
                                ) : (
                                    assetName
                                )}
                            </Card.Meta>
                        )}
                        {(loading || serviceDocs.length > 0) && (
                            <Card.Meta style={{ marginTop: '4px', fontSize: '14px', color: 'black' }}>
                                Attached Docs:{' '}
                                {loading ? (
                                    <Icon name="spinner" loading />
                                ) : (
                                    <>
                                        {serviceDocs.map((doc) => {
                                            const href = getAttachedFileLink(doc);
                                            if (!href) return null;
                                            return (
                                                <a
                                                    key={doc._id || doc.Name}
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ 
                                                        color: '#0066cc', 
                                                        textDecoration: 'underline', 
                                                        marginLeft: '6px',
                                                    }}
                                                >
                                                    <span style={{ fontWeight: 'bold' }}>{doc.Name}</span>
                                                </a>
                                            );
                                        })}
                                    </>
                                )}
                            </Card.Meta>
                        )}
                    </div>
                    {serviceImage && (
                        <div style={{ flexShrink: 0, marginLeft: '12px' }}>
                            <Image
                                style={{ height: '130px', width: '160px', objectFit: 'cover' }}
                                src={serviceImage}
                            />
                        </div>
                    )}
                </Card.Content>
            </Card>
        );
    }

    return (
        <React.Fragment>
            <Card className="cardContainer" style={{ marginTop: "10px", border: source === "placeOrder" ? '1px solid #000000' : '1px solid #A8DDFD', backgroundColor: '#FAF3E0', cursor: 'pointer', width: source === "createOrder" ? "100%" : "380px" }}>
                <Card.Content>
                    {serviceImage && <Image
                        floated='right'
                        size='tiny'
                        style={{ height: '80px', width: "100px" }}
                        src={serviceImage}
                    />}
                    <Card.Header style={{ padding: "0px" }}>{service.name}</Card.Header>
                    <div style={{ marginTop: "5px" }}>
                        <Card.Description>
                            {displayDescription}
                        </Card.Description>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: 'center', width: '100%', marginTop: isShortDescriptionAndNameMaxed ? "10px" : '' }}>
                        <div style={{ width: '50%' }}>
                            {service && service.defaults && service.defaults.externalCost && Number(service.defaults.externalCost) !== 0 && (
                                <Card.Meta style={{ color: "black", fontSize: "15px" }}>${service.defaults.externalCost}</Card.Meta>
                            )}
                        </div>
                    </div>

                    {assetName && (
                        <div style={{ marginTop: '8px' }}>
                            <Card.Meta style={{ color: "black", fontSize: "14px", textAlign: 'right' }}>
                                Asset:{' '}
                                {assetUrl ? (
                                    <a
                                        href={assetUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ color: '#0066cc', textDecoration: 'underline' }}
                                    >
                                        <span style={{ fontWeight: 'bold' }}>{assetName}</span>
                                    </a>
                                ) : (
                                    assetName
                                )}
                            </Card.Meta>
                        </div>
                    )}
                    <div style={{ display: 'flex' }}>
                        <Dimmer active={loading} inverted>
                            <Loader inverted>Loading...</Loader>
                        </Dimmer>
                        {(loading || serviceDocs.length > 0) && (
                            <p style={{ marginLeft: 'auto' }}>
                                Attached Docs:{' '}
                                {loading ? (
                                    <Icon name="spinner" loading />
                                ) : (
                                    <>
                                        {serviceDocs.map((doc) => {
                                            const href = getAttachedFileLink(doc);
                                            if (!href) return null;
                                            return (
                                                <a
                                                    key={doc._id || doc.Name}
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ 
                                                        color: '#0066cc', 
                                                        textDecoration: 'underline', 
                                                        marginLeft: '6px',
                                                    }}
                                                >
                                                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{doc.Name}</span>
                                                </a>
                                            );
                                        })}
                                    </>
                                )}
                            </p>
                        )}
                    </div>
                </Card.Content>
            </Card>
        </React.Fragment>
    );
}

export default ServiceCard